import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { BookingService } from '@app/core/services/booking.service';
import { ToastService } from '@app/core/services/toast.service';
import { AddressService, AddressSuggestion } from '@app/core/services/address.service';
import { environment } from '@environments/environment';

const apiUrl = environment.apiUrl;
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-booking',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss']
})
export class BookingComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  calculatedPrice = 0;
  durationDays = 1;
  durationSeconds = 0;
  durationHours = 0;
  startMinDate = '';
  customerProvidesHelper = false;
  includes360Branding = false;
  showBillingAddress = false;
  acceptedTerms = false;
  acceptedRevocation = false;
  apiUrl = environment.apiUrl;
  
  // Price Breakdown
  priceBreakdown: any = null;
  priceBreakdownDetails: any = null;
  isDevMode = !environment.production;
  
  // Availability Check Properties
  checkingAvailability = false;
  availabilityChecked = false;
  availabilityStatus: 'available' | 'unavailable' | 'partial' | null = null;
  availabilityMessage = '';
  availabilityDetails = '';

  // Address Search Properties
  addressSuggestions: AddressSuggestion[] = [];
  showAddressSuggestions = false;
  calculatingDistance = false;
  private addressSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private bookingService: BookingService,
    private addressService: AddressService,
    private toastService: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    const minStart = new Date();
    minStart.setHours(minStart.getHours() + 24);
    this.startMinDate = minStart.toISOString().split('T')[0];

    this.form = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      companyName: [''],
      phone: ['', [Validators.required, Validators.pattern(/^[\d\s+\-()]*$/)]],
      address: ['', Validators.required],
      street: ['', Validators.required],
      postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      city: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      startDate: ['', Validators.required],
      startTime: ['10:00', Validators.required],
      endDate: ['', Validators.required],
      endTime: ['10:00', Validators.required],
      distance: [0, [Validators.required, Validators.min(0)]],
      notes: [''],
      billingStreet: [''],
      billingPostalCode: [''],
      billingCity: [''],
      includes360Branding: [false],
      brandingNotes: [''],
      customerProvidesHelper: [false],
      acceptedTerms: [false, Validators.requiredTrue],
      acceptedRevocation: [false]
    }, { validators: [this.endAfterStartValidator()] });

    // Auto-fill form in dev mode
    if (this.isDevMode) {
      this.fillWithRandomData();
    }

    // Setup address search with debounce
    this.addressSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 3) {
          return [];
        }
        return this.addressService.searchAddress(query);
      })
    ).subscribe({
      next: (suggestions) => {
        this.addressSuggestions = suggestions;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Address search error:', error);
        this.addressSuggestions = [];
      }
    });
  }

  onAddressInput(event: any) {
    const value = event.target.value;
    if (value.length >= 3) {
      this.addressSearchSubject.next(value);
    } else {
      this.addressSuggestions = [];
    }
  }

  selectAddress(suggestion: AddressSuggestion) {
    this.form.patchValue({ 
      address: suggestion.description,
      city: suggestion.city || '' // Set city from suggestion
    });
    this.showAddressSuggestions = false;
    this.addressSuggestions = [];
    
    // Calculate distance automatically
    this.calculatingDistance = true;
    this.addressService.calculateDistance(suggestion.description).subscribe({
      next: (result) => {
        console.log('Distance calculated:', result);
        this.form.patchValue({ distance: result.distance });
        this.durationSeconds = result.durationSeconds;
        this.calculatingDistance = false;
        
        // Check if booking data is complete
        const isComplete = this.isBookingDataComplete();
        console.log('selectAddress - isBookingDataComplete:', isComplete, {
          startDate: this.form.get('startDate')?.value,
          endDate: this.form.get('endDate')?.value,
          durationDays: this.durationDays
        });
        
        // Rechne und prüfe Verfügbarkeit nur wenn Datum und Zeit gesetzt sind
        if (isComplete) {
          console.log('Calling calculatePrice and autoCheckAvailability');
          this.calculatePrice();
          this.autoCheckAvailability();
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Distance calculation error:', error);
        this.calculatingDistance = false;
        this.toastService.show('Fehler bei der Entfernungsberechnung. Bitte versuchen Sie es erneut.', 'error');
      }
    });
  }

  onDateChange() {
    const startDate = this.form.get('startDate')?.value;
    const startTime = this.form.get('startTime')?.value;
    const endDate = this.form.get('endDate')?.value;
    const endTime = this.form.get('endTime')?.value;
    
    if (startDate && startTime && endDate && endTime) {
      // Kombiniere Datum und Zeit
      const start = new Date(`${startDate}T${startTime}:00`);
      const end = new Date(`${endDate}T${endTime}:00`);
      
      if (end > start) {
        // Berechne Stunden
        const diffMs = end.getTime() - start.getTime();
        this.durationHours = diffMs / (1000 * 60 * 60);
        
        // Berechne Tage (aufrunden auf nächste volle Stunde, dann durch 24 teilen)
        this.durationDays = Math.ceil(this.durationHours / 24);
        
        this.availabilityChecked = false; // Reset availability check when dates change
        
        // Recalculate price if address was already selected (priceBreakdown exists)
        if (this.priceBreakdown) {
          this.calculatePrice();
          this.autoCheckAvailability();
        }
      }
    }

    // re-run validators for cross-field constraints
    this.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
  }

  private autoCheckAvailability() {
    this.checkingAvailability = true;
    this.availabilityChecked = false;
    this.availabilityStatus = null;
    
    const startDate = new Date(this.form.get('startDate')?.value);
    const endDate = new Date(this.form.get('endDate')?.value);

    this.bookingService.getAvailableDates(startDate, endDate).subscribe({
      next: (response: any) => {
        this.checkingAvailability = false;
        this.availabilityChecked = true;
        
        if (response.available) {
          this.availabilityStatus = 'available';
          this.availabilityMessage = 'Der Termin ist verfügbar!';
          this.availabilityDetails = 'Alle Geräte sind in dieser Periode verfügbar.';
        } else {
          this.availabilityStatus = 'unavailable';
          this.availabilityMessage = 'Der Termin ist nicht verfügbar';
          this.availabilityDetails = '';
        }
        
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('Availability check error:', error);
        this.checkingAvailability = false;
        this.availabilityStatus = 'unavailable';
        this.availabilityMessage = 'Verfügbarkeitsprüfung nicht möglich';
        this.availabilityDetails = 'Bitte versuchen Sie es später erneut.';
        this.availabilityChecked = true;
      }
    });
  }

  private endAfterStartValidator(): ValidatorFn {
    return (group: AbstractControl) => {
      const startDate = group.get('startDate')?.value;
      const startTime = group.get('startTime')?.value;
      const endDate = group.get('endDate')?.value;
      const endTime = group.get('endTime')?.value;

      if (!startDate || !startTime || !endDate || !endTime) {
        return null;
      }

      const start = new Date(`${startDate}T${startTime}:00`);
      const end = new Date(`${endDate}T${endTime}:00`);
      if (end <= start) {
        return { endBeforeStart: true };
      }
      return null;
    };
  }

  isAvailabilityCheckReady(): boolean {
    const startDate = this.form.get('startDate')?.value;
    const endDate = this.form.get('endDate')?.value;
    return !!(startDate && endDate && this.durationDays > 0);
  }

  isBookingDataComplete(): boolean {
    const startDate = this.form.get('startDate')?.value;
    const startTime = this.form.get('startTime')?.value;
    const endDate = this.form.get('endDate')?.value;
    const endTime = this.form.get('endTime')?.value;
    return !!(startDate && startTime && endDate && endTime && this.durationDays > 0);
  }

  isReadyForCalculation(): boolean {
    const distance = this.form.get('distance')?.value;
    return this.isBookingDataComplete() && !!(distance && distance > 0);
  }

  checkAvailability() {
    if (!this.isAvailabilityCheckReady()) {
      this.toastService.show('Bitte geben Sie Startdatum und Enddatum ein', 'error');
      return;
    }

    this.checkingAvailability = true;
    this.availabilityChecked = false;
    this.availabilityStatus = null;
    
    const startDate = new Date(this.form.get('startDate')?.value);
    const endDate = new Date(this.form.get('endDate')?.value);

    console.log('Checking availability...', { startDate, endDate });

    this.bookingService.getAvailableDates(startDate, endDate).subscribe({
      next: (response) => {
        console.log('Availability response:', response);
        this.checkingAvailability = false;
        this.availabilityChecked = true;
        
        if (response.available) {
          this.availabilityStatus = 'available';
          this.availabilityMessage = 'Videowand ist verfügbar! ✓';
          this.availabilityDetails = `Die Videowand ist für den Zeitraum vom ${startDate.toLocaleDateString('de-DE')} bis ${endDate.toLocaleDateString('de-DE')} verfügbar.`;
          this.toastService.show('Die Videowand ist in diesem Zeitraum verfügbar!', 'success');
          this.trackAvailabilityCheck('available');
          // Trigger price calculation when available
          this.calculatePrice();
        } else {
          this.availabilityStatus = 'unavailable';
          this.availabilityMessage = 'Videowand nicht verfügbar';
          this.availabilityDetails = 'Die Videowand ist in diesem Zeitraum leider nicht verfügbar.';
          this.toastService.show('Die Videowand ist in diesem Zeitraum nicht verfügbar', 'error');
          this.trackAvailabilityCheck('unavailable');
        }
        console.log('Status updated:', this.availabilityStatus, 'checking:', this.checkingAvailability);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Availability check error:', error);
        this.checkingAvailability = false;
        this.availabilityChecked = true;
        this.availabilityStatus = 'partial';
        this.availabilityMessage = 'Verfügbarkeitsprüfung konnte nicht durchgeführt werden';
        this.availabilityDetails = 'Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.';
        this.toastService.show(
          error.error?.error || 'Fehler bei der Verfügbarkeitsprüfung',
          'error'
        );
        this.trackAvailabilityCheck('error');
      }
    });
  }

  calculatePrice() {
    const distance = this.form.get('distance')?.value || 0;
    const startDateValue = this.form.get('startDate')?.value;
    const startTimeValue = this.form.get('startTime')?.value;
    const endDateValue = this.form.get('endDate')?.value;
    const endTimeValue = this.form.get('endTime')?.value;
    const requestedLocation = this.form.get('address')?.value || '';
    const requestedCity = this.form.get('city')?.value || '';
    const startDate = startDateValue && startTimeValue ? new Date(`${startDateValue}T${startTimeValue}:00`) : undefined;
    const endDate = endDateValue && endTimeValue ? new Date(`${endDateValue}T${endTimeValue}:00`) : undefined;
    
    console.log('📍 Location Data in calculatePrice:', {
      address: this.form.get('address')?.value,
      city: this.form.get('city')?.value,
      requestedLocation,
      requestedCity,
      distance
    });
    
    console.log('calculatePrice called:', {
      distance,
      durationDays: this.durationDays,
      includes360Branding: this.includes360Branding
    });
    
    if (this.durationDays > 0) {
      console.log('Calling bookingService.calculatePrice with includes360Branding:', this.includes360Branding);
      this.bookingService.calculatePrice(
        distance, 
        this.durationDays, 
        this.durationSeconds, 
        this.durationHours, 
        this.customerProvidesHelper, 
        this.includes360Branding,
        startDate,
        endDate,
        requestedLocation,
        requestedCity
      ).subscribe({
        next: (response) => {
          console.log('✅ Price calculation response:', response);
          this.priceBreakdown = response;
          this.calculatedPrice = response.totalPrice;
          console.log('Updated priceBreakdown with includes360Branding:', response.includes360Branding, 'brandingCost:', response.brandingCost);

          this.trackPriceCalculation(response, distance);
          
          // Dev Mode: Detaillierte Kalkulation anzeigen
          if (this.isDevMode) {
            this.priceBreakdownDetails = {
              distance,
              durationDays: this.durationDays,
              durationSeconds: this.durationSeconds,
              durationHours: this.durationHours,
              customerProvidesHelper: this.customerProvidesHelper,
              includes360Branding: this.includes360Branding,
              personCount: this.customerProvidesHelper ? 1 : 2,
              
              // Lieferung & Setup Berechnung
              deliveryKmCost: distance * 4 * 0.3,
              drivingCost: (this.durationSeconds * 4 / 3600) * 40 * (this.customerProvidesHelper ? 1 : 2),
              setupTeardownCost: 1.75 * (this.customerProvidesHelper ? 1 : 2) * 40,
              
              minSetupPrice: 80
            };
          }
          
          // Trigger change detection to update view
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('❌ Price calculation error:', error);
          this.toastService.show('Fehler beim Berechnen des Preises', 'error');
        }
      });
    } else {
      console.warn('⚠️ calculatePrice called but durationDays <= 0');
    }
  }

  private trackPriceCalculation(response: any, distance: number) {
    const matomo = (window as any)._paq as any[] | undefined;
    if (!matomo) {
      return;
    }

    const totalPrice = response?.totalPrice ?? 0;
    const brandingCost = response?.brandingCost ?? 0;
    const rentalCost = response?.rentalCost ?? 0;
    const deliverySetupCost = response?.deliverySetupCost ?? 0;

    matomo.push([
      'trackEvent',
      'Pricing',
      'Calculate',
      `days:${this.durationDays}|km:${Math.round(distance)}|helper:${this.customerProvidesHelper ? 'yes' : 'no'}|branding:${this.includes360Branding ? 'yes' : 'no'}`,
      Math.round(totalPrice)
    ]);

    matomo.push([
      'trackEvent',
      'PricingDetails',
      'Breakdown',
      `rental:${Math.round(rentalCost)}|delivery:${Math.round(deliverySetupCost)}|branding:${Math.round(brandingCost)}`
    ]);
  }

  get isB2C(): boolean {
    return !this.form.get('companyName')?.value || this.form.get('companyName')?.value.trim() === '';
  }

  onSubmit() {
    // Prüfe Widerrufsbelehrung für B2C
    if (this.isB2C && !this.form.get('acceptedRevocation')?.value) {
      this.toastService.show('Bitte akzeptieren Sie die Widerrufsbelehrung', 'error');
      return;
    }
    
    if (this.form.valid && this.durationDays > 0) {
      this.loading = true;
      const formValue = this.form.value;
      
      // Kombiniere Datum und Uhrzeit
      const startDateStr = formValue.startDate;
      const startTimeStr = formValue.startTime;
      const endDateStr = formValue.endDate;
      const endTimeStr = formValue.endTime;
      
      const startDate = new Date(`${startDateStr}T${startTimeStr}:00`);
      const endDate = new Date(`${endDateStr}T${endTimeStr}:00`);
      
      // Prepare line items from price breakdown
      const lineItems = this.prepareLineItems();
      
      const bookingData = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        companyName: formValue.companyName,
        phone: formValue.phone,
        email: formValue.email,
        address: formValue.address,
        street: formValue.street,
        postalCode: formValue.postalCode,
        city: formValue.city,
        billingStreet: formValue.billingStreet,
        billingPostalCode: formValue.billingPostalCode,
        billingCity: formValue.billingCity,
        startDate: startDate,
        endDate: endDate,
        distance: formValue.distance,
        durationSeconds: this.durationSeconds,
        durationDays: this.durationDays,
        notes: formValue.notes,
        includes360Branding: this.includes360Branding,
        brandingNotes: formValue.brandingNotes,
        customerProvidesHelper: this.customerProvidesHelper,
        lineItems: lineItems
      };

      this.bookingService.createBooking(bookingData).subscribe({
        next: (response) => {
          this.loading = false;
          this.trackBookingCompletion(response?.price ?? this.calculatedPrice, response?.bookingId);
          this.toastService.show(
            'Buchung erfolgreich erstellt! Ein Magic Link wurde per E-Mail versendet.',
            'success'
          );
          this.form.reset();
          this.router.navigate(['/booking/confirmation']);
        },
        error: (error) => {
          this.loading = false;
          this.toastService.show(
            error.error?.error || 'Fehler beim Erstellen der Buchung',
            'error'
          );
          this.trackBookingCompletionError();
        }
      });
    }
  }

  private trackAvailabilityCheck(status: 'available' | 'unavailable' | 'error') {
    const matomo = (window as any)._paq as any[] | undefined;
    if (!matomo) {
      return;
    }

    matomo.push([
      'trackEvent',
      'Availability',
      'Check',
      `status:${status}|days:${this.durationDays}|helper:${this.customerProvidesHelper ? 'yes' : 'no'}|branding:${this.includes360Branding ? 'yes' : 'no'}`
    ]);
  }

  private trackBookingCompletion(price: number, bookingId?: string) {
    const matomo = (window as any)._paq as any[] | undefined;
    if (!matomo) {
      return;
    }

    matomo.push([
      'trackEvent',
      'Booking',
      'Complete',
      `days:${this.durationDays}|price:${Math.round(price)}|helper:${this.customerProvidesHelper ? 'yes' : 'no'}|branding:${this.includes360Branding ? 'yes' : 'no'}${bookingId ? `|id:${bookingId}` : ''}`,
      Math.round(price)
    ]);
  }

  private trackBookingCompletionError() {
    const matomo = (window as any)._paq as any[] | undefined;
    if (!matomo) {
      return;
    }

    matomo.push([
      'trackEvent',
      'Booking',
      'CompleteError',
      `days:${this.durationDays}|helper:${this.customerProvidesHelper ? 'yes' : 'no'}|branding:${this.includes360Branding ? 'yes' : 'no'}`
    ]);
  }

  private prepareLineItems(): any[] {
    const lineItems = [];
    
    if (this.priceBreakdown) {
      // Delivery & Setup
      lineItems.push({
        description: `Lieferung & Setup (inkl. Auf-/Abbau${this.priceBreakdown.customerProvidesHelper ? ' mit Helfer' : ''})`,
        quantity: 1,
        unitPrice: this.priceBreakdown.deliverySetupCost,
        totalPrice: this.priceBreakdown.deliverySetupCost
      });
      
      // Rental
      lineItems.push({
        description: `Videowand Miete (${this.durationDays} Tag${this.durationDays !== 1 ? 'e' : ''})`,
        quantity: this.durationDays,
        unitPrice: this.priceBreakdown.rentalCost / this.durationDays,
        totalPrice: this.priceBreakdown.rentalCost
      });
      
      // Branding (if applicable)
      if (this.includes360Branding && this.priceBreakdown.brandingCost > 0) {
        lineItems.push({
          description: '360° Branding',
          quantity: 1,
          unitPrice: this.priceBreakdown.brandingCost,
          totalPrice: this.priceBreakdown.brandingCost
        });
      }
    }
    
    return lineItems;
  }

  fillWithRandomData() {
    const names = ['Max Mustermann', 'Anna Schmidt', 'Tom Weber', 'Lisa Müller', 'Jan Fischer'];
    const companies = ['', 'Eventfirma GmbH', 'Messe AG', 'Marketing Solutions', ''];
    const streets = ['', 'Bahnhofstraße 42', 'Marktplatz 7', 'Lindenweg 23'];
    const cities = ['Berlin', 'Hamburg', 'München', 'Dresden', 'Köln'];
    const postalCodes = ['10115', '20095', '80331', '01067', '50667'];
    const phones = ['+49 151 12345678', '+49 160 98765432', '+49 171 55555555', '+49 152 11112222'];
    const notes = [
      '', 
      'Bitte pünktlich anrufen vor Anlieferung',
      'Wichtige Veranstaltung - auf Qualität achten',
      'Event findet indoor statt'
    ];

    const randomIndex = Math.floor(Math.random() * names.length);
    const cityIndex = Math.floor(Math.random() * cities.length);
    
    // Split name into first and last
    const fullName = names[randomIndex];
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || nameParts[0];
    
    // Set dates (tomorrow to 3 days from now)
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 2);

    this.form.patchValue({
      firstName: firstName,
      lastName: lastName,
      companyName: companies[randomIndex],
      phone: phones[Math.floor(Math.random() * phones.length)],
      email: 'jan@fbox.pics',
      street: streets[Math.floor(Math.random() * streets.length)],
      postalCode: postalCodes[cityIndex],
      city: cities[cityIndex],
      address: `${streets[Math.floor(Math.random() * streets.length)]}, ${postalCodes[cityIndex]} ${cities[cityIndex]}, Deutschland`,
      startDate: startDate.toISOString().split('T')[0],
      startTime: '10:00',
      endDate: endDate.toISOString().split('T')[0],
      endTime: '18:00',
      distance: Math.floor(Math.random() * 50) + 10, // 10-60 km
      notes: notes[Math.floor(Math.random() * notes.length)]
    });

    // Trigger date change to calculate duration
    this.onDateChange();

    // Mark all fields as touched to show validation
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });

    console.log('Dev Mode: Form filled with random data');
  }
}
